import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/clueData";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daim7hjoh653ivfvog4g";
const MAX_CONTENT = 500;

type Status = "idle" | "submitting" | "success" | "error";

export default function SubmitForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [count, setCount] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    data.delete("contact_alt");
    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam") || serverMsg.includes("form data is spam");
      const success = response.ok && parsed?.code === "OK" && !isSpam;

      if (success) {
        setStatus("success");
        form.reset();
        setCount(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试，或检查填写内容是否完整。");
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，内容尚未提交，请稍后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="title" className="block text-sm font-semibold text-foreground-800">
            投稿标题 <span className="text-primary-600">*</span>
          </label>
          <input
            id="title"
            name="title"
            type="text"
            required
            maxLength={60}
            placeholder="用一句话概括你想说的事"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div>
          <label htmlFor="category" className="block text-sm font-semibold text-foreground-800">
            内容分类 <span className="text-primary-600">*</span>
          </label>
          <select
            id="category"
            name="category"
            required
            defaultValue=""
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="" disabled>
              请选择分类
            </option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="occur_time" className="block text-sm font-semibold text-foreground-800">
            发生时间
          </label>
          <input
            id="occur_time"
            name="occur_time"
            type="date"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="location" className="block text-sm font-semibold text-foreground-800">
            发生地点
          </label>
          <input
            id="location"
            name="location"
            type="text"
            placeholder="例如：某市某商圈（可只填到城市级别）"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <fieldset className="sm:col-span-2">
          <legend className="text-sm font-semibold text-foreground-800">投稿方式</legend>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row">
            <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm transition-colors hover:border-primary-300">
              <input type="radio" name="submit_type" value="匿名投稿" defaultChecked className="accent-primary-500" />
              <span>
                <span className="block font-medium text-foreground-900">匿名投稿</span>
                <span className="mt-0.5 block text-xs text-foreground-500">不展示个人信息</span>
              </span>
            </label>
            <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm transition-colors hover:border-primary-300">
              <input type="radio" name="submit_type" value="实名投稿" className="accent-primary-500" />
              <span>
                <span className="block font-medium text-foreground-900">实名投稿</span>
                <span className="mt-0.5 block text-xs text-foreground-500">经同意后再展示</span>
              </span>
            </label>
          </div>
        </fieldset>

        <div className="sm:col-span-2">
          <label htmlFor="content" className="block text-sm font-semibold text-foreground-800">
            正文内容 <span className="text-primary-600">*</span>
            <span className="float-right text-xs font-normal text-foreground-500">
              {count}/{MAX_CONTENT}
            </span>
          </label>
          <textarea
            id="content"
            name="content"
            required
            maxLength={MAX_CONTENT}
            rows={7}
            onChange={(event) => setCount(event.target.value.length)}
            placeholder="请按时间顺序描述事件经过，尽量说明时间、地点、涉及方与你希望解决的问题。请勿填写他人隐私信息。"
            className="mt-2 w-full resize-y rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          ></textarea>
        </div>

        <div>
          <label htmlFor="nickname" className="block text-sm font-semibold text-foreground-800">
            署名 / 网名
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            placeholder="留空则显示为“匿名网友”"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-foreground-800">
            联系邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="仅用于核实与后续沟通，不会公开"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <input
        type="text"
        name="contact_alt"
        className="field-honeypot"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-6 text-foreground-500">
          提交即表示你确认内容真实，并同意我们在核实后进行整理与发布。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              正在提交
            </>
          ) : (
            <>
              <i className="ri-send-plane-line text-base"></i>
              提交投稿
            </>
          )}
        </button>
      </div>

      {status === "success" && (
        <div className="mt-5 flex items-start gap-3 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3">
          <i className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-700"></i>
          <p className="text-sm leading-6 text-secondary-900">
            已收到你的投稿，感谢信任。编辑会在 1 至 3 个工作日内查看，如需补充信息会通过邮箱与你联系。
          </p>
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 flex items-start gap-3 rounded-md border border-primary-300 bg-primary-50 px-4 py-3">
          <i className="ri-error-warning-line mt-0.5 text-lg text-primary-700"></i>
          <p className="text-sm leading-6 text-primary-900">{formError}</p>
        </div>
      )}
    </form>
  );
}